import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled ? "bg-linen/85 backdrop-blur-xl border-b border-line" : "bg-transparent border-b border-transparent"}`}
        data-testid="nav-header"
      >
        <div className="container-x h-[72px] flex items-center justify-between">
          <Logo light={!scrolled} />
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => go(l.id)} data-testid={`nav-link-${l.id}`}
                className={`relative text-[13px] font-medium tracking-wide transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-brass after:transition-[width] after:duration-300 hover:after:w-full ${scrolled ? "text-ink-2 hover:text-brass" : "text-linen/80 hover:text-linen"}`}>
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={() => go("book")} className="hidden sm:inline-flex btn-brass h-11 px-5 text-[13px]" data-testid="nav-cta-btn">Free Measure & Quote</button>
            <button onClick={() => setOpen(true)} className={`lg:hidden p-2 ${scrolled ? "text-ink" : "text-linen"}`} aria-label="Open menu" data-testid="nav-menu-open-btn"><Menu size={24} /></button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-ink/60 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} data-testid="nav-mobile-overlay">
            <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 30 }}
              className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-ink text-linen p-8 flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="nav-mobile-drawer">
              <div className="flex items-center justify-between mb-12">
                <Logo light />
                <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-menu-close-btn"><X size={26} /></button>
              </div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l, i) => (
                  <motion.button key={l.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.06 }}
                    onClick={() => go(l.id)} className="text-left py-4 border-b border-line-dark font-serif text-3xl hover:text-brass transition-colors" data-testid={`nav-mobile-link-${l.id}`}>
                    {l.label}
                  </motion.button>
                ))}
              </div>
              <button onClick={() => go("book")} className="btn-brass mt-auto w-full" data-testid="nav-mobile-cta-btn">Free Measure & Quote</button>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
